if (typeof customElements.get('cart-date-picker') === 'undefined') {
  class CartDatePicker extends HTMLElement {
    constructor() {
      super();
      this._onCheckoutAttempt = this._onCheckoutAttempt.bind(this);
      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onInstructionsInput = this._onInstructionsInput.bind(this);
      this._onInstructionsBlur = this._onInstructionsBlur.bind(this);
    }

    connectedCallback() {
      this.dateInput = this.querySelector('[data-role="date"]');
      this.timeInput = this.querySelector('[data-role="time"]');
      this.notice = this.querySelector('[data-role="notice"]');
      this.monthLabel = this.querySelector('[data-role="month-label"]');
      this.prevBtn = this.querySelector('[data-role="prev-month"]');
      this.nextBtn = this.querySelector('[data-role="next-month"]');
      this.labelsEl = this.querySelector('[data-role="weekday-labels"]');
      this.calendarEl = this.querySelector('[data-role="calendar"]');
      this.slotsLabel = this.querySelector('[data-role="slots-label"]');
      this.slotsEl = this.querySelector('[data-role="slots"]');
      this.instructionsInput = this.querySelector('[data-role="instructions"]');
      this._instructionsTimer = null;

      this.activeWeekdays = this._parseActiveWeekdays(this.dataset.activeWeekdays);

      this.collectionHandle = this.dataset.collectionHandle || '';

      const today = new Date();
      this.today = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      // Earliest selectable delivery date, based on the cut-off window
      // configured in the theme settings (0 = same-day allowed).
      const cutoffDays = Math.max(0, parseInt(this.dataset.cutoffDays, 10) || 0);
      this.minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + cutoffDays);
      this.viewYear = this.minDate.getFullYear();
      this.viewMonth = this.minDate.getMonth();
      this.selectedDate = this.dateInput?.value || '';
      this._clearStaleSelection();

      this.prevBtn.addEventListener('click', () => this._navigateMonth(-1));
      this.nextBtn.addEventListener('click', () => this._navigateMonth(1));
      this.instructionsInput?.addEventListener('input', this._onInstructionsInput);
      this.instructionsInput?.addEventListener('blur', this._onInstructionsBlur);

      this._renderWeekdayLabels();
      this._renderCalendar();
      this._restoreFromCart();

      this._bindCheckout();
      document.addEventListener('krown:cart:update', this._onCartUpdated);
      document.addEventListener('krown:cart:add', this._onCartUpdated);
    }

    disconnectedCallback() {
      this.checkoutForm?.removeEventListener('submit', this._onCheckoutAttempt);
      this.checkoutButton?.removeEventListener('click', this._onCheckoutAttempt);
      document.removeEventListener('krown:cart:update', this._onCartUpdated);
      document.removeEventListener('krown:cart:add', this._onCartUpdated);
      clearTimeout(this._instructionsTimer);
      this.instructionsInput?.removeEventListener('input', this._onInstructionsInput);
      this.instructionsInput?.removeEventListener('blur', this._onInstructionsBlur);
    }

    _navigateMonth(delta) {
      const next = new Date(this.viewYear, this.viewMonth + delta, 1);
      const minMonth = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
      if (next < minMonth) return;
      this.viewYear = next.getFullYear();
      this.viewMonth = next.getMonth();
      this._renderCalendar();
    }

    _renderWeekdayLabels() {
      const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
      // Known Monday used as base so the week starts on Monday.
      const base = new Date(2024, 0, 1);
      this.labelsEl.innerHTML = '';
      for (let i = 0; i < 7; i++) {
        const li = document.createElement('li');
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        li.textContent = fmt.format(d);
        li.style.cssText = 'text-align:center;font-size:.75rem;color:#666;';
        this.labelsEl.appendChild(li);
      }
    }

    _renderCalendar() {
      const monthFmt = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      this.monthLabel.textContent = monthFmt.format(firstOfMonth);

      const minMonth = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
      this.prevBtn.disabled = firstOfMonth <= minMonth;
      this.prevBtn.style.opacity = this.prevBtn.disabled ? '.3' : '';
      this.prevBtn.style.cursor = this.prevBtn.disabled ? 'default' : 'pointer';

      // Pad with blanks so day-of-week aligns with the labels row.
      // Week starts on Monday, so convert Sunday (0) to index 6.
      const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();

      this.calendarEl.innerHTML = '';
      for (let i = 0; i < leadingBlanks; i++) {
        const spacer = document.createElement('span');
        this.calendarEl.appendChild(spacer);
      }
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(this.viewYear, this.viewMonth, day);
        const iso = this._iso(date);
        const isBeforeCutoff = date < this.minDate;
        const isDeliveryDay = this.activeWeekdays.has(date.getDay());
        const isInactive = isBeforeCutoff || !isDeliveryDay;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = String(day);
        btn.dataset.date = iso;
        btn.style.cssText =
          'padding:.5rem;border:1px solid transparent;border-radius:6px;background:transparent;cursor:pointer;' +
          (isInactive ? 'opacity:.3;pointer-events:none;' : '');
        if (iso === this.selectedDate && !isInactive) {
          btn.style.background = 'var(--cdp-accent)';
          btn.style.color = 'var(--cdp-accent-text)';
          btn.setAttribute('aria-pressed', 'true');
        }
        if (!isInactive) btn.addEventListener('click', () => this._selectDay(iso, btn));
        this.calendarEl.appendChild(btn);
      }

      if (this.selectedDate) {
        const inView = this.calendarEl.querySelector(`[data-date="${this.selectedDate}"]`);
        if (inView) this._renderSlots(this.selectedDate);
        else this._clearSlots();
      }
    }

    _selectDay(iso, btn) {
      this.selectedDate = iso;
      this.calendarEl.querySelectorAll('button').forEach((el) => {
        el.style.background = 'transparent';
        el.style.color = '';
        el.setAttribute('aria-pressed', 'false');
      });
      btn.setAttribute('aria-pressed', 'true');
      btn.style.background = 'var(--cdp-accent)';
      btn.style.color = 'var(--cdp-accent-text)';

      if (this.dateInput) this.dateInput.value = iso;
      if (this.timeInput) this.timeInput.value = '';
      this._renderSlots(iso);
    }

    _renderSlots(iso) {
      this.slotsLabel.style.display = '';
      this.slotsEl.innerHTML = '';

      const todayIso = this._iso(this.today);
      const isToday = iso === todayIso;
      const now = new Date();
      const periods = ['AM', 'PM'].filter((period) => {
        if (!isToday) return true;
        return period !== 'AM' || now.getHours() < 12;
      });

      if (!periods.length) {
        this.slotsEl.textContent = this.dataset.emptyMessage || 'No times available on this day.';
        return;
      }

      const selectedTime = this.timeInput?.value || '';
      periods.forEach((period) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = period;
        btn.style.cssText =
          'padding:.4rem .75rem;border:1px solid var(--cdp-border);border-radius:999px;background:transparent;cursor:pointer;';
        if (period === selectedTime) {
          btn.style.background = 'var(--cdp-accent)';
          btn.style.color = 'var(--cdp-accent-text)';
          btn.style.borderColor = 'var(--cdp-accent)';
          btn.setAttribute('aria-pressed', 'true');
        }
        btn.addEventListener('click', () => this._selectSlot(period, btn, { persist: true }));
        this.slotsEl.appendChild(btn);
      });
    }

    _parseActiveWeekdays(raw) {
      // Expects a comma-separated list of weekday indices (0 = Sunday … 6 = Saturday).
      // Only the days configured in the theme settings are active; an empty
      // selection means no delivery days are available.
      const days = (raw || '')
        .split(',')
        .map((value) => value.trim())
        .filter((value) => value !== '')
        .map(Number)
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);
      return new Set(days);
    }

    _parseIso(iso) {
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(y, m - 1, d);
    }

    _clearSlots() {
      this.slotsLabel.style.display = 'none';
      this.slotsEl.innerHTML = '';
    }

    async _selectSlot(time, btn, { persist = true } = {}) {
      this.slotsEl.querySelectorAll('button').forEach((el) => {
        el.style.background = 'transparent';
        el.style.color = '';
        el.style.borderColor = 'var(--cdp-border)';
        el.setAttribute('aria-pressed', 'false');
      });
      btn.setAttribute('aria-pressed', 'true');
      btn.style.background = 'var(--cdp-accent)';
      btn.style.color = 'var(--cdp-accent-text)';
      btn.style.borderColor = 'var(--cdp-accent)';

      if (this.timeInput) this.timeInput.value = time;
      this._hideNoticeIfComplete();

      if (!persist) return;

      await this._persistAttributes({ 'Delivery Time': time });
    }

    _getCartAttributes() {
      return {
        'Delivery Date': this.dateInput?.value || '',
        'Delivery Time': this.timeInput?.value || '',
        'Delivery Instructions': this.instructionsInput?.value || '',
      };
    }

    async _persistAttributes(overrides = {}) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { ...this._getCartAttributes(), ...overrides } }),
      });
    }

    _onInstructionsInput() {
      this._hideNoticeIfComplete();
      clearTimeout(this._instructionsTimer);
      this._instructionsTimer = setTimeout(() => this._persistAttributes(), 500);
    }

    _onInstructionsBlur() {
      clearTimeout(this._instructionsTimer);
      this._persistAttributes();
    }

    // Drops a previously saved delivery date that no longer satisfies the
    // cut-off window (e.g. the cart was created before the cut-off passed),
    // so customers can't check out with a date that is no longer available.
    _clearStaleSelection() {
      const date = this.dateInput?.value;
      if (!date) return;
      const parsed = this._parseIso(date);
      if (!Number.isNaN(parsed.getTime()) && parsed >= this.minDate) return;

      this.selectedDate = '';
      if (this.dateInput) this.dateInput.value = '';
      if (this.timeInput) this.timeInput.value = '';
      this._clearSlots();
      this._persistAttributes({ 'Delivery Date': '', 'Delivery Time': '' });
    }

    _restoreFromCart() {
      this._clearStaleSelection();
      const date = this.dateInput?.value;
      const time = this.timeInput?.value;
      if (!date) return;

      const parsed = this._parseIso(date);
      if (Number.isNaN(parsed.getTime())) return;

      this.viewYear = parsed.getFullYear();
      this.viewMonth = parsed.getMonth();
      this.selectedDate = date;
      this._renderCalendar();

      if (time) {
        const slotBtn = [...this.slotsEl.querySelectorAll('button')].find((b) => b.textContent === time);
        if (slotBtn) this._selectSlot(time, slotBtn, { persist: false });
      }
    }

    _iso(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }

    _bindCheckout() {
      this.checkoutForm?.removeEventListener('submit', this._onCheckoutAttempt);
      this.checkoutButton?.removeEventListener('click', this._onCheckoutAttempt);

      const selector = this.getAttribute('data-checkout-selector');
      if (!selector) return;

      this.checkoutButton = document.querySelector(selector);
      if (!this.checkoutButton) return;

      const formId = this.checkoutButton.getAttribute('form');
      this.checkoutForm = formId ? document.getElementById(formId) : this.checkoutButton.closest('form');

      if (this.checkoutForm) {
        this.checkoutForm.addEventListener('submit', this._onCheckoutAttempt);
      } else {
        this.checkoutButton.addEventListener('click', this._onCheckoutAttempt);
      }
    }

    _isComplete() {
      const instructions = (this.instructionsInput?.value || '').trim();
      return Boolean(this.dateInput?.value && this.timeInput?.value && instructions);
    }

    _hideNoticeIfComplete() {
      if (this._isComplete()) this._hideNotice();
    }

    _onCheckoutAttempt(event) {
      if (this.hidden) return;
      if (this._isComplete()) return;

      event.preventDefault();

      const hasDate = Boolean(this.dateInput?.value);
      const hasTime = Boolean(this.timeInput?.value);
      const instructions = (this.instructionsInput?.value || '').trim();

      let message;
      if (hasDate && hasTime && !instructions) {
        message =
          this.getAttribute('data-missing-instructions-message') ||
          'Please fill out the delivery instructions field before checking out.';
      } else {
        message = this.getAttribute('data-missing-message') || 'Please choose a delivery date and time.';
      }

      this._showNotice(message);

      if (!instructions) {
        this.instructionsInput?.focus();
      }
    }

    async _onCartUpdated() {
      this._bindCheckout();
      await this._refreshVisibility();
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const date = cart?.attributes?.['Delivery Date'] || '';
        const time = cart?.attributes?.['Delivery Time'] || '';
        const instructions = cart?.attributes?.['Delivery Instructions'] || '';
        if (this.dateInput && this.dateInput.value !== date) this.dateInput.value = date;
        if (this.timeInput && this.timeInput.value !== time) this.timeInput.value = time;
        if (this.instructionsInput && this.instructionsInput.value !== instructions) {
          this.instructionsInput.value = instructions;
        }
        if (date && time && instructions.trim()) this._hideNotice();
        this._restoreFromCart();
      } catch (_) {}
    }

    async _refreshVisibility() {
      if (!this.collectionHandle) {
        this.hidden = false;
        return;
      }
      try {
        // Re-render the snippet server-side via the helper-cart section so
        // collection membership is re-evaluated against the current cart.
        const response = await fetch('?section_id=helper-cart');
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('cart-date-picker');
        if (!fresh) return;
        this.hidden = fresh.hasAttribute('hidden');
        this.dataset.collectionProductIds = fresh.getAttribute('data-collection-product-ids') || '';
      } catch (_) {}
    }

    _showNotice(message) {
      if (!this.notice) return;
      this.notice.textContent = message;
      this.notice.style.display = '';
      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    _hideNotice() {
      if (!this.notice) return;
      this.notice.style.display = 'none';
      this.notice.textContent = '';
    }
  }

  customElements.define('cart-date-picker', CartDatePicker);
}
